import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogOut, MessageCircle, IndianRupee, ArrowLeft, UserRound } from "lucide-react";
import { getCurrentUser, signInWithGoogle, signOut } from "@/lib/auth";
import { openWhatsApp, openPaymentPage, DEFAULT_WA_MESSAGE } from "@/lib/whatsapp";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  service_request: "Care Request",
  caretaker_application: "Caretaker Application",
  nurse_application: "Nurse Application",
  vendor_registration: "Vendor Registration",
};

const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const Account = () => {
  const [user, setUser] = useState(undefined);
  const [requests, setRequests] = useState(null);

  useEffect(() => {
    getCurrentUser().then((u) => setUser(u || null));
  }, []);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/leads/mine`, { withCredentials: true })
      .then(({ data }) => setRequests(data))
      .catch(() => setRequests([]));
  }, [user]);

  const logout = async () => {
    await signOut();
    setUser(null);
    setRequests(null);
  };

  return (
    <main className="bg-alabaster text-navy font-body min-h-screen" data-testid="account-page">
      <header className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="account-logo-link">
            <img src="/logo.jpeg" alt="Sharpin Healthcare Services" className="h-11 w-11 rounded-full object-cover ring-2 ring-tealbrand/20" />
            <div className="leading-tight">
              <span className="font-display font-bold text-lg tracking-tight text-navy block">SHARPIN</span>
              <span className="text-[10px] uppercase tracking-[0.3em] text-tealbrand font-semibold">Healthcare</span>
            </div>
          </Link>
          <Link to="/" data-testid="account-back-home" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-tealbrand transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to Website
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-16">
        {user === undefined ? (
          <div className="flex justify-center py-20" data-testid="account-loading">
            <Loader2 className="h-8 w-8 animate-spin text-tealbrand" />
          </div>
        ) : !user ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-3xl border border-slate-200 shadow-xl p-10 text-center"
          >
            <div className="h-14 w-14 rounded-2xl bg-tealbrand-light flex items-center justify-center mx-auto mb-6">
              <UserRound className="h-7 w-7 text-tealbrand-dark" />
            </div>
            <h1 className="font-display text-2xl font-bold text-navy mb-2">Your Sharpin Account</h1>
            <p className="text-sm text-slate-500 mb-8 max-w-sm mx-auto leading-relaxed">
              Sign in with Google to track your care requests, bookings and payments in one place.
            </p>
            <button
              onClick={() => signInWithGoogle("/account")}
              data-testid="account-signin-btn"
              className="inline-flex items-center justify-center gap-3 bg-navy text-white font-semibold px-8 py-3.5 rounded-full hover:bg-tealbrand transition-colors"
            >
              <GoogleIcon /> Sign in with Google
            </button>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="bg-white rounded-3xl border border-slate-200 p-8 mb-8 flex flex-wrap items-center gap-5" data-testid="account-profile">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="h-16 w-16 rounded-full object-cover ring-2 ring-tealbrand/30" referrerPolicy="no-referrer" />
              ) : (
                <div className="h-16 w-16 rounded-full bg-tealbrand-light flex items-center justify-center">
                  <UserRound className="h-7 w-7 text-tealbrand-dark" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="font-display text-xl font-bold text-navy truncate" data-testid="account-name">{user.name || "Sharpin Customer"}</h1>
                <p className="text-sm text-slate-500 truncate" data-testid="account-email">{user.email}</p>
                {user.role === "admin" && (
                  <span className="inline-block mt-1 text-[11px] font-bold uppercase tracking-wider bg-navy text-white px-2.5 py-1 rounded-full">Admin</span>
                )}
              </div>
              <button
                onClick={logout}
                data-testid="account-logout-btn"
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-red-600 transition-colors"
              >
                <LogOut className="h-4 w-4" /> Sign Out
              </button>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mb-10">
              <Link
                to="/"
                onClick={() => setTimeout(() => document.getElementById("join")?.scrollIntoView({ behavior: "smooth" }), 400)}
                data-testid="account-request-care-btn"
                className="bg-navy text-white rounded-2xl p-5 text-sm font-semibold hover:bg-tealbrand transition-colors"
              >
                Request Care
              </Link>
              <button
                onClick={() => openWhatsApp(DEFAULT_WA_MESSAGE)}
                data-testid="account-whatsapp-btn"
                className="bg-white border border-slate-200 rounded-2xl p-5 text-sm font-semibold text-navy hover:border-tealbrand transition-colors inline-flex items-center gap-2"
              >
                <MessageCircle className="h-4 w-4 text-tealbrand" /> WhatsApp Us
              </button>
              <button
                onClick={openPaymentPage}
                data-testid="account-pay-btn"
                className="bg-white border border-slate-200 rounded-2xl p-5 text-sm font-semibold text-navy hover:border-tealbrand transition-colors inline-flex items-center gap-2"
              >
                <IndianRupee className="h-4 w-4 text-tealbrand" /> Pay Now
              </button>
            </div>

            <h2 className="font-display text-lg font-bold text-navy mb-4">My Requests</h2>
            {requests === null ? (
              <div className="flex justify-center py-12" data-testid="account-requests-loading">
                <Loader2 className="h-6 w-6 animate-spin text-tealbrand" />
              </div>
            ) : requests.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="account-requests-empty">
                No requests linked to this account yet. Submit a form while signed in and it will appear here.
              </div>
            ) : (
              <div className="space-y-4" data-testid="account-requests-list">
                {requests.map((lead) => (
                  <div key={lead.id} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={`account-request-${lead.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <span className="text-xs font-bold uppercase tracking-[0.15em] bg-tealbrand-light text-tealbrand-dark px-3 py-1.5 rounded-full">
                        {TYPE_LABELS[lead.type] || lead.type}
                      </span>
                      <span className="text-xs text-slate-400">{new Date(lead.created_at).toLocaleString("en-IN")}</span>
                    </div>
                    <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                      {Object.entries(lead.data).filter(([k]) => k !== "Account Email").map(([k, v]) => (
                        <div key={k}>
                          <dt className="text-[11px] uppercase tracking-wider font-bold text-slate-400">{k}</dt>
                          <dd className="text-sm text-navy font-medium break-words">{v || "—"}</dd>
                        </div>
                      ))}
                    </dl>
                    {lead.advance_paid && (
                      <p className="mt-3 text-xs font-bold text-tealbrand-dark">₹500 booking advance paid ✓</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>

      <Footer />
    </main>
  );
};

export default Account;
